import {useRequiredConfig} from '@domain/shifthandover'
import {
  CategoryDropDown,
  PriorityDropDown,
  EventProcessStageAutocomplete,
  ProcessStageDropDown
} from '@hconnect/common/components/shiftEventFormFields'
import {EventHighlight} from '@hconnect/common/components/shiftEventFormFields/EventHighlight'
import {REGULAR_SIZE, INPUT_FORM_FIELD_SX} from '@hconnect/common/consts'
import {
  EquipmentIdType,
  EventType,
  Priority,
  ParameterChangeData,
  BaseEvent,
  ParameterChange,
  Schedule,
  ShiftEvent,
  Approver
} from '@hconnect/common/types'
import {useRequiredUser} from '@hconnect/login'
import {Grid, TextField, Box} from '@mui/material'
import {EventDescriptionBox} from '@widgets/common-shift-event'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {getParameterChangeStates} from '../../utils'
import {EquipmentSearchContainer} from '../eventProperties'
import {ApproversField} from '../eventProperties/ApproversField'
import {EventInfo} from '../eventProperties/EventInfo'
import {EventTypeDropdownBox} from '../eventProperties/EventTypeDropdownBox'
import {ParameterChangeStatusDropDown} from '../eventProperties/ParameterChangeStatusDropDown'
import {ScheduleSwitch} from '../eventProperties/ScheduleSwitch'

type TypeOfParameterChange = ParameterChange

type PossibleErrors =
  | keyof TypeOfParameterChange
  | 'equipmentNumber'
  | 'approvers'
  | 'parameterChange.currentValue'
  | 'parameterChange.newValue'

type ParameterChangeFormProps = {
  plantId: string
  item: Partial<TypeOfParameterChange>
  update: <K extends keyof TypeOfParameterChange>(
    key: K,
    value: TypeOfParameterChange[K] | EventType
  ) => void
  updateMultiPart: (delta) => void
  validationError: Map<PossibleErrors, string>
  originalApprovers?: Approver[]
  equipmentIdType?: EquipmentIdType
  isEditMode: boolean
  children: React.ReactNode
  autocompleteProcessStage?: boolean
}

export const parameterChangeDefaultObject = (): Partial<ShiftEvent> => ({
  description: '',
  eventType: 'parameterChange',
  status: 'pending',
  title: '',
  parameterChange: {
    currentValue: '',
    newValue: ''
  },
  approvers: [],
  needsApproval: false
})

export const ParameterChangeForm: React.FC<ParameterChangeFormProps> = ({
  plantId,
  children,
  item,
  update,
  updateMultiPart,
  validationError,
  originalApprovers,
  isEditMode,
  equipmentIdType,
  autocompleteProcessStage
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const user = useRequiredUser()
  const config = useRequiredConfig(plantId)

  const getErrorText = (key: PossibleErrors) => {
    const errorKey = validationError.get(key)
    return errorKey && t(errorKey)
  }

  const setParameterChangeData = (value: Partial<ParameterChangeData>) => {
    updateMultiPart({
      parameterChange: {
        ...item.parameterChange,
        ...value
      }
    })
  }

  const {disableField, disableNeedsApproval, disableApproversField} = useMemo(
    () =>
      getParameterChangeStates(
        isEditMode,
        item.needsApproval,
        (item as ShiftEvent)?.createdBy?.id,
        user.id,
        item.approvers,
        item.status === 'waitingForApproval'
      ),
    [item, user.id, isEditMode]
  )

  return (
    <>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          data-test-id="event-form-title"
          error={validationError.has('title')}
          fullWidth={true}
          helperText={getErrorText('title')}
          label={t('shiftEvent.label.title')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            update('title', event.target.value)
          }
          required
          value={item.title}
          variant="filled"
          InputProps={INPUT_FORM_FIELD_SX}
          disabled={disableField}
        />

        <EventHighlight
          title={t('eventsTable.action.highlight')}
          value={item.isHighlighted}
          onChange={(value) => update('isHighlighted', value)}
          disabled={disableField}
        />
      </Box>
      {'created' in item && (
        <EventInfo
          item={item as BaseEvent}
          workOrderTarget={config.workOrderTarget}
          plantId={plantId}
        />
      )}
      <Box my={1}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item {...REGULAR_SIZE}>
            <EventTypeDropdownBox
              item={item as BaseEvent}
              value={'parameterChange'}
              updateMultiPart={updateMultiPart}
            />
          </Grid>
          <Grid item {...REGULAR_SIZE}>
            <ParameterChangeStatusDropDown
              translate={t}
              eventData={item as ShiftEvent}
              data-test-id="event-form-status"
              errorText={getErrorText('status')}
              onChange={(value) => update('status', value)}
              disabled={disableField}
            />
          </Grid>

          <Grid item xs={12}>
            <EquipmentSearchContainer
              plantId={plantId}
              data-test-id="event-form-equipmentNumber"
              errorText={getErrorText('equipmentNumber')}
              updateMultiPart={updateMultiPart}
              value={item as BaseEvent}
              disabled={disableField}
              equipmentIdType={equipmentIdType}
            />
          </Grid>

          {autocompleteProcessStage ? (
            <Grid item xs={12}>
              <EventProcessStageAutocomplete
                plantId={plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value?: string) => update('processStage', value)}
                value={item.processStage}
              />
            </Grid>
          ) : (
            <Grid item {...REGULAR_SIZE}>
              <ProcessStageDropDown
                translate={t}
                plantId={config.plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value) => update('processStage', value)}
                value={item.processStage}
                disabled={disableField}
              />
            </Grid>
          )}
          <Grid item {...REGULAR_SIZE}>
            <CategoryDropDown
              translate={t}
              data-test-id="event-form-category"
              errorText={getErrorText('category')}
              onChange={(value) => update('category', value)}
              required
              value={item.category}
              disabled={disableField}
            />
          </Grid>
          <Grid item {...REGULAR_SIZE}>
            <PriorityDropDown
              translate={t}
              data-test-id="event-form-priority"
              errorText={getErrorText('priority')}
              onChange={(value) => update('priority', value)}
              value={item.priority as Priority}
              disabled={disableField}
            />
          </Grid>
          <ScheduleSwitch
            label={t('shiftEvent.action.schedule')}
            disabled={disableField}
            item={item}
            onChange={(value: Schedule | undefined) => update('schedule', value)}
            errorText={getErrorText('schedule')}
            plantId={plantId}
          />
          <Grid item xs={12}>
            <TextField
              required
              data-test-id="event-form-parameter-current"
              error={validationError.has('parameterChange.currentValue')}
              fullWidth={true}
              helperText={getErrorText('parameterChange.currentValue')}
              label={t('shiftEvent.label.currentValue')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setParameterChangeData({currentValue: event.target.value})
              }}
              value={item.parameterChange?.currentValue}
              variant="filled"
              InputProps={INPUT_FORM_FIELD_SX}
              disabled={disableField}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              data-test-id="event-form-parameter-new"
              error={validationError.has('parameterChange.newValue')}
              fullWidth={true}
              helperText={getErrorText('parameterChange.newValue')}
              label={t('shiftEvent.label.newValue')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setParameterChangeData({newValue: event.target.value})
              }
              value={item.parameterChange?.newValue}
              variant="filled"
              InputProps={INPUT_FORM_FIELD_SX}
              disabled={disableField}
            />
          </Grid>
          <ApproversField
            plantId={plantId}
            toggleApprovers={(needsApproval) => {
              updateMultiPart({
                needsApproval,
                approvers: needsApproval ? originalApprovers : undefined
              })
              update('status', needsApproval ? 'waitingForApproval' : 'pending')
            }}
            onChange={(approvers) => {
              updateMultiPart({approvers})
            }}
            errorText={getErrorText('approvers')}
            approvers={item.approvers || []}
            needsApproval={item.needsApproval}
            disableNeedsApproval={disableNeedsApproval}
            disableApproversField={disableApproversField}
          />
          <Grid item xs={12}>
            <EventDescriptionBox
              current={item.description || ''}
              errorCode={validationError.get('description')}
              onChange={(next) => update('description', next)}
              required
              disabled={disableField}
            />
          </Grid>
        </Grid>
      </Box>
      {children}
    </>
  )
}
